import type { AccountDetailsSection } from './types';

type AccountDetailsFieldKind =
  | 'text'
  | 'number'
  | 'date'
  | 'datetime'
  | 'check'
  | 'list'
  | 'person'
  | 'record'
  | 'account'
  | 'link'
  | 'phone'
  | 'address';

export interface AccountDetailsField {
  label: string;
  kind: AccountDetailsFieldKind;
  // Omitted for the Account's own id, which sits at the top of the response.
  section?: AccountDetailsSection;
  key: string;
}

interface AccountDetailsLayoutSection {
  title: string;
  left: Array<AccountDetailsField>;
  right: Array<AccountDetailsField>;
}

const field =
  (section: AccountDetailsSection) =>
  (key: string, label: string, kind: AccountDetailsFieldKind = 'text'): AccountDetailsField => ({
    section,
    key,
    label,
    kind,
  });

const info = field('account_information');
const strategic = field('strategic_details');
const address = field('address_information');
const channel = field('channel_manager_information');
const notes = field('notes');
const chain = field('chain_and_ownership');
const lpc = field('lpc_partnership');
const operational = field('operational_attributes');
const closure = field('closure_details');
const property = field('property_information');
const system = field('system_details');
const systemInfo = field('system_information');

// Order, titles and columns follow the mockup. The API groups fields by Salesforce layout, so a few
// fields (LPC margins, vendor funded duration) are read from a different section than they show in.
export const ACCOUNT_DETAILS_LAYOUT: Array<AccountDetailsLayoutSection> = [
  {
    title: 'Account Information',
    left: [
      info('name', 'Account Name'),
      info('owner', 'Account Owner', 'person'),
      info('currency', 'Account Currency'),
      info('red_flag', 'Account Red Flag', 'check'),
      info('red_flag_date', 'Account Red Flag Date', 'date'),
      info('red_flag_reason', 'Account Red Flag Reason'),
      info('number_of_date_changes', 'Number of Date Changes'),
      info('cancellation_policy', 'Cancellation Policy'),
      info('date_change_allowed', 'Date Change Allowed', 'number'),
      info('educational_package_status', 'Educational Package Status'),
      info('master_status', 'Account Master Status'),
      info('last_closed_won_flash_date', 'Last Closed Won Date (Flash)', 'date'),
      info('open_flash_opportunity_created_at', 'Open Flash Opp Created Date', 'date'),
    ],
    right: [
      { key: 'salesforce_id', label: '18 Character ID', kind: 'text' },
      info('record_type', 'Account Record Type'),
      info('account_type', 'Type'),
      info('activity_status', 'Activity Status'),
      info('partner_engagement_status_flash', 'Partner Engagement Status (Flash)'),
      info('partner_engagement_status_lpc', 'Partner Engagement Status (LPC)'),
      info('proposed_engagement_tier', 'Proposed Engagement Tier'),
      info('weighting', 'Account Weighting'),
      info('top_property', 'Top Property', 'check'),
      info('latest_dynamic_simplified_opportunity_at', 'Latest Dynamic Simplified Opp', 'datetime'),
      info('current_opportunity', 'Current Opportunity', 'record'),
      info('product_types', 'Product Type', 'list'),
    ],
  },
  {
    title: 'Strategic Details',
    left: [
      strategic('billing_country', 'Billing Country'),
      strategic('strategic_territory', 'Strategic Territory'),
      strategic('rating', 'Rating'),
      strategic('territory_campaign_manager', 'Territory Campaign Manager', 'person'),
      strategic('territory_market_manager', 'Territory Market Manager', 'person'),
      strategic('territory_connectivity_account_manager', 'Territory Connectivity Account Manager', 'person'),
    ],
    right: [
      strategic('partner_target', 'Partner Target'),
      strategic('latest_deal_end_date', 'Latest Deal End Date', 'date'),
      strategic('closed_won_flash_opportunities', 'Number of Closed Won Flash Opps', 'number'),
      strategic('not_suitable_reason', 'Reasons for not Suitable'),
      strategic('latest_run_at', 'Latest Run Date', 'datetime'),
      strategic('territory_target', 'Territory Target', 'record'),
      strategic('territory_target_tier', 'Territory Target Tier'),
      strategic('territory_target_period', 'Territory Target Period'),
      strategic('deal_category', 'Deal Category'),
      strategic('deal_sub_category', 'Deal Sub Category'),
      strategic('deal_destination', 'Deal Destination'),
      strategic('deal_sub_destination', 'Deal Sub - Destination'),
    ],
  },
  {
    title: 'Address Information',
    left: [
      address('tripadvisor_url', 'TripAdvisor Link', 'link'),
      address('billing_address', 'Billing Address', 'address'),
    ],
    right: [address('website', 'Website', 'link'), address('phone', 'Phone', 'phone')],
  },
  {
    title: 'Channel Manager Information',
    left: [channel('channel_managers', 'Channel Manager', 'list')],
    right: [channel('other_channel_manager', 'Other Channel Manager')],
  },
  {
    title: 'Notes',
    left: [notes('notes', 'Notes')],
    right: [],
  },
  {
    title: 'Chain & Ownership Details',
    left: [
      chain('parent_account', 'Parent Account', 'account'),
      chain('child_count', 'Child Count', 'number'),
      chain('hotel_code', 'Hotel Code'),
      chain('lux_plus_discount_active', 'Lux+ Discount Active', 'check'),
    ],
    right: [chain('asset_manager', 'Asset Manager', 'record'), chain('hotel_owner', 'Hotel Owner', 'record')],
  },
  {
    title: 'LPC Partnership Details',
    left: [
      lpc('lux_plus_discussed', 'Lux+ Discussed', 'check'),
      lpc('lux_plus_discount_feedback', 'Lux+ Discount Feedback'),
      lpc('lux_plus_last_updated_on', 'Lux Plus+ Last Update Date', 'date'),
      lpc('lux_plus_vendor_funded_discounts', 'Lux+ Vendor Funded Discounts', 'check'),
      lpc('lux_plus_vendor_funded_from', 'Lux+ Vendor Funded Active From', 'date'),
      lpc('lux_plus_vendor_funded_to', 'Lux+ Vendor Funded Active to', 'date'),
      info('lux_plus_vendor_funded_duration', 'Lux Vendor Funded Duration', 'number'),
      lpc('lux_plus_vendor_funded_discount_reason', 'Lux+ VF Discount Reason'),
      lpc('lux_plus_always_on_inclusions', 'Lux+ Always On Inclusions', 'check'),
      lpc('lux_plus_discount_override', 'Le Override Lp Plus Discount', 'number'),
      info('lpc_margin_180_days', 'LPC margin 180 day', 'number'),
      info('lpc_margin_percent_180_days', 'LPC margin perc 180', 'number'),
    ],
    right: [
      lpc('logged_in_member_discounts', 'LE Logged In Member Discounts', 'check'),
      lpc('logged_in_member_inclusions', 'LE Logged In Member inclusions', 'check'),
      lpc('has_package_rates', 'Has Package Rates', 'check'),
      lpc('promotion_status', 'Promotion Status'),
      lpc('last_promotion_end_date', 'Last Promotion End Date', 'date'),
      lpc('next_promotion_end_date', 'Next Promotion End Date', 'date'),
      lpc('promotion_ends_in_days', 'Promotion Ends Days', 'number'),
      lpc('extranet_status', 'Extranet Status'),
      lpc('extranet_last_login_at', 'Extranet Last Login Date', 'datetime'),
    ],
  },
  {
    title: 'Operational Attributes',
    left: [operational('can_reduce_inventory', 'Can Reduce Inventory', 'check')],
    right: [],
  },
  {
    title: 'Closure Details',
    left: [closure('closed_from', 'Closed From', 'date'), closure('closed_until', 'Closed Until', 'date')],
    right: [],
  },
  {
    title: 'Property Information Details',
    left: [
      property('check_in_out_time', 'Check-in/out time'),
      property('hotel_last_renovation', 'Hotel Last Renovation'),
      property('year_opened', 'Year Opened'),
      property('recent_awards', 'Recent Awards/Accolades'),
      property('local_attractions_shuttle', 'Local Attractions Shuttle'),
      property('location_attractions', 'Location Attractions'),
      property('shop_onsite', 'Shop Onsite'),
      property('closest_airport', 'Closest Airport'),
      property('airport_transfer_cost', 'Airport Transfer Cost'),
    ],
    right: [
      property('parking', 'Parking'),
      property('kids_club', 'Kids Club'),
      property('babysitting', 'Babysitting'),
      property('disability_access', 'Disability Access'),
      property('wheelchair_accessible_rooms', 'Rooms with Wheelchair access'),
      property('wifi', 'Wi-fi'),
      property('smoking_policy', 'Smoking Policy'),
    ],
  },
  {
    title: 'System Details',
    left: [
      system('created_by', 'Created By', 'person'),
      system('last_activity_for_escapes_at', 'Last Activity Date for Escapes', 'datetime'),
      system('last_activity_logged_against_parent', 'Last Activity Logged Against Parent', 'check'),
      system('days_since_last_activity', 'Days Since Last Activity', 'number'),
      system('mm_activity_at', 'MM Activity Date', 'datetime'),
      system('mm_days_since_last_activity', 'MM - Days Since Last Activity', 'number'),
      system('pcm_activity_at', 'PCM Activity Date', 'datetime'),
      system('pcm_days_since_last_activity', 'PCM - Days Since Last Activity', 'number'),
      info('pcm_mm_not_actioned', 'PCM_MM_Not_Actioned'),
    ],
    right: [
      system('legacy_account_id', 'AccountId'),
      system('protected_by', 'Protected By User', 'person'),
      system('assigned_pod', 'Assigned Pod'),
      system('last_modified_by', 'Last Modified By', 'person'),
    ],
  },
  {
    title: 'System Information',
    left: [
      systemInfo('last_closed_won_deal_end_at', 'Last Closed Won Deal End Date', 'date'),
      systemInfo('reactivation_account', 'Reactivation Account', 'check'),
    ],
    right: [],
  },
];
